import React, { Component } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import ProgressBar from '@ramonak/react-progress-bar';
import { getMusicEngineEpisodeDetail, getUnifiedCurrentUser, musicEngineToggleFavorite, musicEnginePost } from '../../api/api';
import PageSpinner from 'components/Spinner/PageSpinner';
import { logEventPagesViewed, logEventEpisodeListened, logEventEpisodeLiked } from 'utils/analytics';
import Header from 'components/Header/Header';
import pauseButton from 'assets/pause-button.png';
import forward from 'assets/forward.png';
import backward from 'assets/backward.png';
import playButton from 'assets/play.png';
import volumeUp from 'assets/volume-up.png';
import volumeDown from 'assets/volume-down.png';
import like from 'assets/heart-color.png';
import unlike from 'assets/heart-black.png';
import Style from './episodePage.module.css';
import defaultImage from 'assets/logo_short.png';
import './style.scss';

class EpisodePage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            podcast: null,
            episode: null,
            likes: 0,
            playStatus: false,
            likeStatus: false,
            currentPlayTime: 0,
            mediaLength: 0,
            loadingText: '',
            errorMessage: ''
        };
        this.audioPlayer = React.createRef();
        this.loadingAnimation = null;
    // Accumulate listening seconds across pauses
    this.accumulatedListeningSeconds = 0;
    }

    async componentDidMount() {
        // Track page view (non-blocking)
        logEventPagesViewed(null, window.location.href, 'episodepage_page', 'EpisodePage').catch(() => {});
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        await this.fetchUser();
        await this.getEpisodeAndPodcast();
    }

    componentWillUnmount() {
        if (this.loadingAnimation) clearInterval(this.loadingAnimation);
        // Clean up listening timer
        if (this.listeningTimer) {
            clearInterval(this.listeningTimer);
            this.listeningTimer = null;
        }
        // Clean up listening tracking
        this.resetListeningTracking();
    }

    fetchUser = async () => {
        try {
            const { user } = await getUnifiedCurrentUser();
            this.setState({ user });
        } catch (error) {
        }
    };

    getEpisodeAndPodcast = async () => {
        const { id, episode: episodeId } = this.props.match.params;
        // Fetch from MusicEngine endpoint: /api/episode/{episodeId}
        try {
            const me = await getMusicEngineEpisodeDetail(episodeId);
            const meData = me?.data || me; // support either {data: {...}} or direct
            const ep = meData?.episode || meData;
            if (ep) {
                // Map fields to local expectations
                const audioUrl = ep.audio_url || ep.audio || ep.audioUrl || ep.media_url || ep.stream_url || ep.enclosure_url || (ep.enclosure && ep.enclosure.url) || ep.url || '';
                const thumb = ep.image || ep.artwork || ep.artwork_url || (ep.podcast && (ep.podcast.image || ep.podcast.artwork || ep.podcast.artwork_url)) || '';
                const episode = {
                    _id: ep.id || ep._id || episodeId,
                    title: ep.title || ep.name || '',
                    url: audioUrl,
                    duration: ep.duration || 0,
                    thumbnailURL80x80: thumb,
                };
                const podcast = {
                    _id: id,
                    title: (ep.podcast && (ep.podcast.title || ep.podcast.name)) || '',
                    author: (ep.podcast && (ep.podcast.author || ep.podcast.creator)) || '',
                    categories: (ep.podcast && ep.podcast.categories) || ''
                };
                const duration = parseInt(episode.duration || 0, 10);
                const likesCount = (typeof ep.loves === 'number') ? ep.loves : (ep.likes || 0);
                const isFavorite = (typeof ep.favorite === 'boolean') ? ep.favorite : !!ep.isLike;
                this.setState({ podcast, episode, mediaLength: duration, likes: likesCount, likeStatus: !!isFavorite });
                // Track episode listened event
                logEventEpisodeListened(this.state.user, {
                    PodcastId: id,
                    EpisodeId: episodeId,
                    Category: podcast.categories || '',
                    PodcastName: podcast.title || '',
                    Author: podcast.author || '',
                    MediaURL: episode.url
                });
                return;
            }
        } catch (error) {
        }
    };

    volumeUpDown = (action) => {
        if (navigator && navigator.volumeManager) {
            navigator.volumeManager[`request${action.charAt(0).toUpperCase() + action.slice(1)}`]();
        }
    };

    playPause = () => {
        const { playStatus, mediaLength } = this.state;
        const audio = this.audioPlayer.current.audioEl.current;
        if (audio && !mediaLength) {
            this.setState({ mediaLength: Math.floor(audio.duration || 0) });
        }
    if (playStatus) {
      audio.pause();
      // On pause, accumulate elapsed time and stop ticking without resetting progress
      if (this.listeningStartTime) {
        this.accumulatedListeningSeconds += Math.floor((Date.now() - this.listeningStartTime) / 1000);
        this.listeningStartTime = 0;
      }
      if (this.listeningTimer) {
        clearInterval(this.listeningTimer);
        this.listeningTimer = null;
      }
    } else {
            audio.play();
            // Start listening timer when playing
            if (!this.listeningTimer) {
                this.listeningTimer = setInterval(() => {
                    this.trackListeningTime();
                }, 1000);
            }
        }
        this.setState({ playStatus: !playStatus });
    };

    updateLoadingText = () => {
        this.setState((prevState) => ({
            loadingText: prevState.loadingText.length === 3 ? '.' : prevState.loadingText + '.'
        }));
    };

    likeHandler = async () => {
        if (!this.state.user) {
            this.setState({ errorMessage: 'Please sign in to like the episode' });
            setTimeout(() => this.setState({ errorMessage: '' }), 5000);
            return;
        }
        try {
            this.loadingAnimation = setInterval(this.updateLoadingText, 500);
            const episodeId = this.state.episode && (this.state.episode._id || this.state.episode.id);
            const meToken = localStorage.getItem('me_access_token');
            if (meToken && episodeId) {
                // Use MusicEngine favorite toggle for episodes
                await musicEngineToggleFavorite({ id: episodeId, objectType: 'episode', action: !this.state.likeStatus });
            }
            
            // Track episode liked event
            logEventEpisodeLiked(this.state.user, {
                EpisodeId: this.state.episode._id,
                PodcastId: this.state.podcast._id,
                PodcastName: this.state.podcast.title || '',
                Author: this.state.podcast.author || '',
                Category: this.state.podcast.categories || '',
                MediaURL: this.state.episode.url
            });
            
            this.setState(prevState => ({
                likeStatus: !prevState.likeStatus,
                likes: prevState.likeStatus ? Math.max(0, prevState.likes - 1) : prevState.likes + 1,
                loadingText: ''
            }));
        } catch (error) {
            this.setState({ loadingText: '' });
        } finally {
            if (this.loadingAnimation) clearInterval(this.loadingAnimation);
        }
    };

    onLoadedMetadata = (e) => {
        const duration = Math.floor(e.target.duration || 0)
        if (duration) {
            this.setState({ mediaLength: duration });
        }
    };

    onListen = () => {
        this.setState(prevState => ({ currentPlayTime: prevState.currentPlayTime + 1 }));
        this.trackListeningTime();
    };

    // Reset listening time tracking when episode changes or user seeks
    resetListeningTracking = () => {
        this.listeningStartTime = 0;
    this.accumulatedListeningSeconds = 0;
        this.listeningTime = 0;
        this.lastRecordedTime = 0;
        this.hasTrackedListening = false;
    };

    // Track listening time and call API when 5 minutes are reached
    trackListeningTime = async () => {
        const { episode, playStatus } = this.state;
        
        // Don't track if not playing or no episode
        if (!playStatus || !episode) {
            return;
        }

        // Reset tracking if episode changed
        if (this.currentEpisodeId !== episode._id) {
            this.currentEpisodeId = episode._id;
            this.resetListeningTracking();
            return;
        }

        // Start tracking when playback begins
        if (this.listeningStartTime === 0) {
            this.listeningStartTime = Date.now();
        }

    // Calculate listening time (in seconds) = accumulated + current session
    const currentTime = Date.now();
    const currentSession = this.listeningStartTime
      ? Math.floor((currentTime - this.listeningStartTime) / 1000)
      : 0;
    this.listeningTime = this.accumulatedListeningSeconds + currentSession;

        // Record listening time every 5 seconds for performance
        if (this.listeningTime - this.lastRecordedTime >= 5) {
            this.lastRecordedTime = this.listeningTime;
        }

        // Call API when 5 minutes (300 seconds) are reached
        if (this.listeningTime >= 300 && !this.hasTrackedListening) {
            this.hasTrackedListening = true;
            await this.trackEpisodeListening();
        }
    };

    // Call API to track episode listening
    trackEpisodeListening = async () => {
        const { episode, user } = this.state;
        
        if (!user || !episode) {
            return;
        }

        try {
            // Prefer MusicEngine endpoint to award listen points
            const meToken = localStorage.getItem('me_access_token');
            const data = { episode_id: Number(episode._id), seconds: this.listeningTime };
            await musicEnginePost('/auth/user/episode/listened', data);
        } catch (error) {
        }
    };

    jump = (direction) => {
        const { currentPlayTime, mediaLength } = this.state;
        const seconds = direction === 'forward' ? 5 : -5;
        const newPlayTime = Math.max(0, Math.min(mediaLength, currentPlayTime + seconds));
        this.audioPlayer.current.audioEl.current.currentTime = newPlayTime;
        this.setState({ currentPlayTime: newPlayTime });
        
        // Reset listening tracking when user seeks
        this.resetListeningTracking();
    };

    formatTime = (time) => {
        if (isNaN(time)) return '00:00';
        const minutes = String(Math.floor(time / 60)).padStart(2, '0');
        const seconds = String(Math.floor(time % 60)).padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    render() {
        const { playStatus, episode, likeStatus, likes, currentPlayTime, mediaLength, loadingText, errorMessage } = this.state;
        const innerHeight = window.innerHeight;
        const innerWidth = window.innerWidth;

        return (
            <div className={Style.container} style={{ height: innerHeight, width: innerWidth }}>
                {!episode ? (
                    <PageSpinner />
                ) : (
                    <>
                        <div className={Style.headerComponent}>
                            <Header title={episode.title || 'Episode'} back={true} backHandler={() => this.props.history.goBack()} />
                            {errorMessage && <span style={{ marginTop: 10, color: 'red', fontSize: '0.8rem' }}>{errorMessage}</span>}
                            <div className={Style.podcastImage}>
                                <img src={episode.thumbnailURL80x80 || defaultImage} alt='podcastImage' className={Style.podcastBanner} />
                            </div>
                            <div className={Style.likeButton} onClick={this.likeHandler}>
                                <span>
                                    <img src={likeStatus ? like : unlike} alt='' className={Style.likeImage} />
                                    <span>{likes || ''} Like {loadingText}</span>
                                </span>
                            </div>
                            <div>
                                {playStatus ? 'not playing' : 'now playing'}
                                <br />
                                {currentPlayTime}
                                <br />
                                {mediaLength}
                                <br />
                                {this.formatTime(mediaLength)}
                                <br />
                                {this.audioPlayer ? 'Y' : 'N'}
                            </div>
                        </div>
                        <div className={Style.mediaContainer}>
                            <div className={Style.headerComponent}>
                                <div className={Style.controlContainer}>
                                    {episode.url && (
                                        <>
                                            <div className={Style.playerWrapper} onClick={() => this.volumeUpDown('down')}>
                                                <img src={volumeDown} className={Style.volumeButton} alt='volume down' />
                                            </div>
                                            <div className={Style.playerWrapper} onClick={() => this.jump('backward')}>
                                                <img src={backward} className={Style.playButton} alt='backward' />
                                            </div>
                                            <div className={Style.playerWrapper} onClick={this.playPause}>
                                                <img src={playStatus ? pauseButton : playButton} className={Style.playButton} alt='play/pause' />
                                            </div>
                                            <div className={Style.playerWrapper} onClick={() => this.jump('forward')}>
                                                <img src={forward} className={Style.playButton} alt='forward' />
                                            </div>
                                            <div className={Style.playerWrapper} onClick={() => this.volumeUpDown('up')}>
                                                <img src={volumeUp} className={Style.volumeButton} alt='volume up' />
                                            </div>
                                        </>
                                    )}
                                </div>
                                {episode.url && (
                                    <div className={Style.progressBarContainer}>
                                        <span className={Style.duration}>{this.formatTime(currentPlayTime)}</span>
                                        <ProgressBar
                                            className='progressBarWrapper'
                                            customLabel=' '
                                            completed={Math.round((currentPlayTime * 100) / mediaLength)}
                                            bgColor='#442253'
                                            height='5px'
                                            width='100%'
                                        />
                                        <span className={Style.duration}>{this.formatTime(mediaLength)}</span>
                                    </div>
                                )}
                                {episode.url && (
                                    <ReactAudioPlayer
                                        ref={this.audioPlayer}
                                        src={episode.url}
                                        autoPlay={false}
                                        listenInterval={1000}
                                        onListen={this.onListen}
                                        onLoadedMetadata={this.onLoadedMetadata}
                                    />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export default EpisodePage;